import {
    Filter,
    ConsumedResourceDocument,
    HandoverProtocol,
    InventoryFilter,
    ItemsForUserByDate,
    ItemsForWarehouse,
    PassportSumPriceRequestDTO,
    ReturnedItem,
    ReturnedItemsGroup,
    TableData,
    Warehouse,
} from '../models/table-models';
import {
    TableRequestParams,
    transformNoPagerResponse,
    transformOnlyItemsResponse,
    transformTableResponse,
} from './data-hooks';
import interceptor from '../restServiceInterceptor';
import {
    AnyPassport,
    Batch,
    BatteryPassport,
    CompletedHandoutGroup,
    HandoutRequest,
    HandoutPreparation,
    HandoutRequestsRevision,
    HandoutReservationRequest,
    HandoutReservationResponse,
    HandoverProps,
    InventoryContent,
    InventoryContentForTask,
    ItemQuantity,
    ManualHandoutRequest,
    OilPassport,
    Passport,
    PassportItemType,
    PassportType,
    QuantityCountList,
    RedistributeItemQuantityRequest,
    RentalAgreementProps,
    Scrap,
    ShortDevicePassport,
    WarehouseReceiptProps,
    HandoversSummary
} from '../models/warehouse-models';
import { date } from './Enums/Common';
import dateFormat from 'dateformat';
import { transformPriceToString } from './helper-functions';
import { DistributorTurnover, Turnover } from '../models/user-models';
import { InventoryCheckup, ItemRequest } from '../models/dashboard-models';
import { Pagination } from "./pagination-hooks";
import { toMinorUnits } from './money';

export const useGetWarehouse = (warehouseId: string): Promise<Warehouse> => {
    return interceptor.get('/warehouse-service/api/warehouse/getWarehouseById', {
        params: {
            warehouseId,
        },
    });
};
export const useGetWarehouses = (props: TableRequestParams): Promise<TableData<Warehouse[]>> => {
    let searchBy = props.filter?.search ? props.filter.search : null;
    return interceptor.get('/warehouse-service/api/warehouse/getAllWarehouses', {
        params: {
            page: props['pager.page'],
            itemsPerPage: props['pager.itemsPerPage'],
            searchBy,
        },
        transformResponse: transformTableResponse,
    });
};

export const useGetReturnedItems = (props: TableRequestParams): Promise<TableData<ReturnedItemsGroup[]>> => {
    let searchBy = props.filter?.search ? props.filter.search : null;
    return interceptor.get('/warehouse-service/api/inventory/getAllReturnedItems', {
        params: {
            page: props['pager.page'],
            itemsPerPage: props['pager.itemsPerPage'],
            searchBy,
            warehouseId: props.filter?.id,
        },
        transformResponse: transformTableResponse,
    });
};

export const useGetConsumedResources = (props: TableRequestParams): Promise<TableData<ConsumedResourceDocument[]>> => {
    let searchBy = props.filter?.search ? props.filter.search : null;
    return interceptor.get('/warehouse-service/api/consumed-resources', {
        params: {
            page: props['pager.page'],
            itemsPerPage: props['pager.itemsPerPage'],
            searchBy,
            warehouseId: props.filter?.id,
        },
        transformResponse: transformTableResponse,
    });
};

export const returnItem = (returnedItem: ReturnedItem) => {
    return interceptor.post(`/warehouse-service/api/inventory/returnItemFromUser?${returnedItem.userId && `id=${returnedItem.userId}`}`, returnedItem, {});
};

export const returnItemToUser = (id: number, userId: number) => {
    return interceptor.post(`/warehouse-service/api/inventory/returnItemToUser`, { id, userId }, {});
};

export const useGetReferences = (warehouseIds: string[], userIds: string[], filter: Filter): Promise<ItemsForWarehouse[]> => {
    let searchBy = filter?.search ? filter.search : null;
    let requestParams = new URLSearchParams();
    warehouseIds.map((id) => requestParams.append('warehouseIds', id));
    return interceptor.get(`/warehouse-service/api/resources/getWarehouseReferences?${requestParams}&userIds=${userIds}`, {
        params: {
            fromDate: filter?.after,
            toDate: filter?.before,
            searchBy,
        },
        transformResponse: transformNoPagerResponse,
    });
};

// Single warehouse reference fetch - for lazy loading per tab
export const useGetSingleWarehouseReference = (warehouseId: string, userIds: string[], filter: Filter, showAll: boolean): Promise<ItemsForWarehouse[]> => {
    let searchBy = filter?.search ? filter.search : null;
    return interceptor.get(`/warehouse-service/api/resources/getWarehouseReferences?warehouseIds=${warehouseId}&userIds=${userIds}`, {
        params: {
            fromDate: filter?.after,
            toDate: filter?.before,
            searchBy,
            showAll,
        },
        transformResponse: transformNoPagerResponse,
    });
};

export const useGetReferencesForDistributor = (userIds: string[], filter: Filter, searchByState: string, selectedType?: string): Promise<ItemsForUserByDate[]> => {
    let searchBy = filter?.search ? filter.search : null;

    return interceptor.get(`/warehouse-service/api/inventory/inventoryReference?userIds=${userIds}`, {
        params: {
            // page: props['pager.page'],
            // itemsPerPage: props['pager.itemsPerPage'],
            searchBy,
            searchByState,
            selectedType,
            fromDate: filter?.after,
            toDate: filter?.before,
        },
        transformResponse: transformNoPagerResponse,
    });
};

// Single distributor reference fetch - for lazy loading per tab
export const useGetSingleDistributorReference = (userId: string, filter: Filter, searchByState: string, selectedType?: string, showAll?: boolean): Promise<ItemsForUserByDate[]> => {
    let searchBy = filter?.search ? filter.search : null;

    return interceptor.get(`/warehouse-service/api/inventory/inventoryReference?userIds=${userId}`, {
        params: {
            searchBy,
            searchByState,
            selectedType,
            fromDate: filter?.after,
            toDate: filter?.before,
            showAll: showAll ?? false,
        },
        transformResponse: transformNoPagerResponse,
    });
};

export const useGetHandoverProtocols = (props: TableRequestParams, branchId?: number): Promise<TableData<HandoverProtocol[]>> => {
    let searchBy = props.filter?.search ? props.filter.search : null;
    const taskCategory = props.filter?.type ? props.filter.type : null;
    return interceptor.get(`/warehouse-service/docs/getAllHandovers`, {
        params: {
            page: props['pager.page'],
            itemsPerPage: props['pager.itemsPerPage'],
            searchBy,
            fromDate: props.filter?.after,
            toDate: props.filter?.before,
            branchId,
            distributorId: props.filter?.id,
            taskCategory,
            sortBy: props.filter?.sortBy,
            sortDirection: props.filter?.sortDirection,
        },
        transformResponse: transformNoPagerResponse,
    });
};

export const getHandoversPDF = (ids: number[]): Promise<Blob> => {
    return interceptor.post(
        'warehouse-service/docs/allHandoversPDF?ids=' + ids.join(','),
        { ids },
        {
            responseType: 'blob',
            transformResponse: (data) => {
                return new Blob([data], { type: 'application/pdf' });
            },
        },
    );
};

export const getHandoversPDFByFilter = (
    props: TableRequestParams,
    branchId?: number
): Promise<Blob> => {
    let searchBy = props.filter?.search ? props.filter.search : null;
    const taskCategory = props.filter?.type ? props.filter.type : null;

    return interceptor.post(
        'warehouse-service/docs/allHandoversPDF/filter',
        {},
        {
            params: {
                from: props.filter?.after,
                to: props.filter?.before,
                branchId,
                searchBy,
                distributorId: props.filter?.id,
                taskCategory,
            },
            responseType: 'blob',
            transformResponse: (data) => {
                return new Blob([data], { type: 'application/pdf' });
            },
        },
    );
};

export const getHandoversExcelByFilter = (
    props: TableRequestParams,
    branchId?: number
): Promise<Blob> => {
    let searchBy = props.filter?.search ? props.filter.search : null;
    const taskCategory = props.filter?.type ? props.filter.type : null;

    return interceptor.get(
        '/warehouse-service/docs/handovers/export/excel',
        {
            params: {
                from: props.filter?.after,
                to: props.filter?.before,
                branchId,
                searchBy,
                distributorId: props.filter?.id,
                taskCategory,
                sortBy: props.filter?.sortBy,
                sortDirection: props.filter?.sortDirection,
            },
            responseType: 'blob',
            transformResponse: (data) => {
                return new Blob([data], {
                    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                });
            },
        },
    );
};

export const useDeleteWarehouse = (fields: Warehouse) => {
    return interceptor.delete('/warehouse-service/api/warehouse/deleteWarehouse', {
        data: {
            warehouseId: fields.id,
        },
    });
};

export const useUpdateWarehouse = (fields: Warehouse, id: number | undefined) => {
    return interceptor.put(`/warehouse-service/api/warehouse/updateWarehouse?id=${id}`, {
        ...fields,
    });
};

export const useAddWarehouse = (fields: Warehouse) => {
    return interceptor.post('/warehouse-service/api/warehouse/addWarehouse', {
        ...fields,
        users: fields.users ?? [],
    });
};

export const useGetAllPassportTypes = (): Promise<PassportType[]> => {
    return interceptor.get('/warehouse-service/api/resources/getAllPassportTypes');
};

export const useGetPassportTypesForWarehouse = (warehouseId: string | undefined): Promise<PassportType[]> => {
    return interceptor.get('/warehouse-service/api/resources/getPassportTypesInWarehouse', {
        params: {
            warehouseId,
        },
    });
};

export const useGetAllQuantities = (props: TableRequestParams, aggregateBatches?: boolean): Promise<TableData<ItemQuantity[]>> => {
    let searchBy = props.filter?.search ? props.filter.search : null;
    return interceptor.get('/warehouse-service/api/resources/getAllQuantities', {
        params: {
            warehouseId: props.filter?.id,
            page: props['pager.page'],
            itemsPerPage: props['pager.itemsPerPage'],
            searchBy,
        },
        // transformResponse: aggregateBatches ? formatCostField : formatQuantitiesByPassport,
        transformResponse: formatCostField,
    });
};

export const UseAddQuantity = (fields: ItemQuantity, warehouseId: string | undefined) => {
    fields.cost *= 100;
    return interceptor.post('/warehouse-service/api/resources/insertItemQuantity', {
        ...fields,
        passportId: fields.passport.passportId,
        warehouseId: warehouseId,
    });
};

export const useDeleteQuantity = (fields: ItemQuantity, deleteReason: string, comment: string, userId?: number) => {
    return interceptor.delete('/warehouse-service/api/resources/deleteItemQuantity', {
        params: {
            id: fields.id,
            deleteReason: deleteReason,
            comment: comment,
        },
    });
};

export const redistributeItemQuantity = (fields: RedistributeItemQuantityRequest) => {
    return interceptor.post('/warehouse-service/api/resources/redistributeItemQuantity', fields);
};

export const UseUpdateQuantity = (fields: ItemQuantity, id: number | undefined) => {
    fields.cost *= 100;
    return interceptor.put(`/warehouse-service/api/resources/updateItemQuantity`, {
        ...fields,
        id: id,
        passportId: fields.passport.passportId,
    });
};

export const useGetAllShortPassports = (forSale: boolean): Promise<ShortDevicePassport[]> => {
    return interceptor.get('/warehouse-service/api/resources/getAllShortPassports', {
        transformResponse: transformNoPagerResponse,
    });
};

export const getPassportByPassportIdAndType = (
    passportId: number,
    type: PassportItemType,
): Promise<ShortDevicePassport> => {
    return interceptor.get('/warehouse-service/api/resources/getPassportByPassportIdAndType', {
        params: {
            passportId,
            type,
        },
    });
};

export const useGetAllShortDevicePassports = (forSale: boolean): Promise<ShortDevicePassport[]> => {
    return interceptor.get('/warehouse-service/api/resources/getAllShortDevicePassports?forSale=' + forSale, {
        transformResponse: transformNoPagerResponse,
    });
};
export const useGetAllShortDevicePassportsWithoutSaleFilter = (): Promise<ShortDevicePassport[]> => {
    return interceptor.get('/warehouse-service/api/resources/getAllShortDevicePassports', {
        transformResponse: transformNoPagerResponse,
    });
};
export const useGetAllShortMaterialPassports = (forSale: boolean): Promise<ShortDevicePassport[]> => {
    return interceptor.get('/warehouse-service/api/resources/getAllShortMaterialsPassports?forSale=' + forSale, {
        transformResponse: transformNoPagerResponse,
    });
};

export const useGetAllShortOtherMaterialPassports = (forSale: boolean, assigneeId: number | undefined): Promise<ShortDevicePassport[]> => {
    return interceptor.get('/warehouse-service/api/resources/getAllShortOtherMaterialsPassports?forSale=' + forSale, {
        params: {
            // assigneeId //removed so that the endpoint returns all other passports
        },
        transformResponse: transformNoPagerResponse,
    });
};

export const useGetAllOilPassports = (forSale: boolean): Promise<OilPassport[]> => {
    return interceptor.get('/warehouse-service/api/resources/getAllPassportsByType?type=OIL&forSale=' + forSale, {
        transformResponse: transformOnlyItemsResponse,
    });
};

export const useGetAllBatteriesPassports = (forSale: boolean): Promise<BatteryPassport[]> => {
    return interceptor.get('/warehouse-service/api/resources/getAllPassportsByType?type=BATTERY&forSale=' + forSale, {
        transformResponse: transformOnlyItemsResponse,
    });
};

export const useUpdatePassport = (fields: AnyPassport) => {
    return interceptor.put(`/warehouse-service/api/resources/updatePassport`, {
        id: fields.id,
        price: toMinorUnits(fields.price),
        priceForInvoice: toMinorUnits(fields.priceForInvoice),
        priceForSale: toMinorUnits(fields.priceForSale),
        minimumDeposit: fields.type === 'DEVICE' || fields.type === 'OTHER' ? toMinorUnits(fields.minimumDeposit ?? 0) : undefined,
        name: fields.name,
        model: fields.forModel ?? fields.model,
        essencePriceGroupId: fields.essencePriceGroupId,
        deviceGroupId: fields.deviceGroupId,
        reedInstallationGroupId: fields.reedInstallationGroupId,
        consumableServiceGroupId: fields.consumableServiceGroupId,
        forDeviceModel: fields.forDeviceModel,
        trackingType: fields.trackingType,
        activeSubstanceWeightGrams: fields.activeSubstanceWeightGrams,
        emptyPackageWeightGrams: fields.emptyPackageWeightGrams,
        childPassportIds: fields.childPassportIds,
        containerType: fields.containerType,
        capacityMl: fields.capacityMl,
        serviceable: fields.serviceable,
        servicePeriodMonths: fields.serviceable ? fields.servicePeriodMonths : null,
        serviceConsumablePassportId: fields.serviceable ? fields.serviceConsumablePassportId : null,
        serviceConsumableCount: fields.serviceable ? fields.serviceConsumableCount : null,
        resources: fields.resources,
    });
};

export const updatePassportArchived = (fields: Pick<Passport, 'id' | 'archived'>) => {
    return interceptor.put('/warehouse-service/api/resources/updatePassportArchived', {
        id: fields.id,
        archived: fields.archived,
    });
};

export const postAddPassport = (fields: AnyPassport): Promise<AnyPassport> => {
    return interceptor.post('/warehouse-service/api/resources/insertPassport', {
        ...fields,
        price: toMinorUnits(fields.price),
        priceForInvoice: toMinorUnits(fields.priceForInvoice),
        priceForSale: toMinorUnits(fields.priceForSale),
        minimumDeposit: fields.type === 'DEVICE' || fields.type === 'OTHER' ? toMinorUnits(fields.minimumDeposit ?? 0) : undefined,
    });
};

export const useDeletePassport = (fields: Passport) => {
    return interceptor.delete('/warehouse-service/api/resources/deletePassport', {
        data: {
            id: fields.id,
        },
    });
};

export const formatPriceField = (data: string) => {
    const r = JSON.parse(data);
    r.items.map((value: { price: number }) => (value.price /= 100));
    r.items.map((value: { priceForInvoice: number }) => (value.priceForInvoice /= 100));
    r.items.map((value: { priceForSale: number }) => (value.priceForSale /= 100));
    r.items.map((value: AnyPassport) => {
        if ((value.type === 'DEVICE' || value.type === 'OTHER') && typeof value.minimumDeposit === 'number') {
            value.minimumDeposit /= 100;
        }
        return value;
    });
    return r;
};
export const formatTurnover = (data: string) => {
    const r = JSON.parse(data);
    const turnovers = (r.turnovers ?? []).map((turnover: DistributorTurnover) => ({
        userId: turnover.userId,
        sum: transformPriceToString(turnover.sum),
        currentSum: transformPriceToString(turnover.currentSum ?? turnover.sum),
        openingSum: transformPriceToString(turnover.openingSum ?? 0),
        paidCashSum: transformPriceToString(turnover.paidCashSum ?? 0),
        paidCardSum: transformPriceToString(turnover.paidCardSum ?? 0),
        paidBankSum: transformPriceToString(turnover.paidBankSum ?? 0),
    }));
    const turnoverList = r.turnoverList.map((turnover: Turnover) => ({
        ...turnover,
    }));
    // Празна сума идва от справките без обобщения (експортът) - там числото не се чете.
    const unpaidBankSum = r.unpaidBankSum == null ? 0 : transformPriceToString(r.unpaidBankSum);
    return { ...r, sum: transformPriceToString(r.sum), unpaidBankSum,
        paidCashSum: transformPriceToString(r.paidCashSum ?? 0),
        paidCardSum: transformPriceToString(r.paidCardSum ?? 0),
        paidBankSum: transformPriceToString(r.paidBankSum ?? 0),
        paidOpeningSum: transformPriceToString(r.paidOpeningSum ?? 0),
        paidBankCurrentSum: transformPriceToString(r.paidBankCurrentSum ?? 0),
        unpaidBankCurrentSum: transformPriceToString(r.unpaidBankCurrentSum ?? 0),
        unpaidOpeningSum: r.unpaidOpeningSum == null ? 0 : transformPriceToString(r.unpaidOpeningSum), turnovers, turnoverList };
};
export const formatCostField = (data: string) => {
    const r = JSON.parse(data);
    r.items.map((value: { cost: number }) => (value.cost /= 100));
    return r;
};

export const formatQuantitiesByPassport = (data: string) => {
    const parsedData = JSON.parse(data);
    const items = parsedData.items;

    const aggregatedItems: { [key: number]: ItemQuantity } = {};

    items.forEach((item: ItemQuantity) => {
        if (aggregatedItems[item.passport.passportId]) {
            aggregatedItems[item.passport.passportId].count += item.count;
        } else {
            aggregatedItems[item.passport.passportId] = { ...item, count: item.count };
        }
    });

    const formattedItems = Object.values(aggregatedItems).map(item => {
        item.cost /= 100;
        return item;
    });

    return { ...parsedData, items: formattedItems };
};

// export const formatQuantitiesByPassport = (data: string) => {
//     const parsedData = JSON.parse(data);
//     const items = parsedData.items;
//
//     const aggregatedItems: { [key: string]: ItemQuantity[] } = {};
//
//     items.forEach((item: ItemQuantity) => {
//         if (aggregatedItems[item.passport.passportId]) {
//             aggregatedItems[item.passport.passportId].push(item);
//         } else {
//             aggregatedItems[item.passport.passportId] = [item];
//         }
//     });
//
//     const formattedItems = Object.values(aggregatedItems).map(itemArray => {
//         return itemArray.map(item => {
//             item.cost /= 100;
//             return item;
//         });
//     });
//
//     return {
//         items: formattedItems,
//     };
// };

export const formatInventoryContentByPassport = (data: string) => {
    const parsedData = JSON.parse(data);
    const items = parsedData.items;

    const aggregatedItems: { [key: number]: InventoryContent } = {};

    items.forEach((item: InventoryContent) => {
        if (aggregatedItems[item.idOfPassport]) {
            aggregatedItems[item.idOfPassport].count += item.count;
            if (aggregatedItems[item.idOfPassport].availableAmount && item.availableAmount) {
                // @ts-ignore
                aggregatedItems[item.idOfPassport].availableAmount += item.availableAmount;
            }
        } else {
            aggregatedItems[item.idOfPassport] = { ...item, count: item.count, availableAmount: item.availableAmount };
        }
    });

    return { ...parsedData, items: Object.values(aggregatedItems) };
};

export const useGetAllPassportsByType = (props: TableRequestParams): Promise<TableData<AnyPassport[]>> => {
    const searchBy = props.filter?.search ? props.filter.search : null;
    const type = props.filter?.type ? props.filter.type : null;
    const archived = props.filter?.archived;
    return interceptor.get('/warehouse-service/api/resources/getAllPassportsByType', {
        params: {
            page: props['pager.page'],
            itemsPerPage: props['pager.itemsPerPage'],
            searchBy,
            type,
            archived,
        },
        transformResponse: formatPriceField,
    });
};

export const getPassportsBySapCodes = (sapCodes: string[]): Promise<AnyPassport[]> => {
    return interceptor.post('/warehouse-service/api/resources/getPassportsBySapCodes', sapCodes);
};

export const getPassportsByDescriptions = (descriptions: string[]): Promise<AnyPassport[]> => {
    return interceptor.post('/warehouse-service/api/resources/getPassportsByDescriptions', descriptions);
};

export interface WarehousePdfImportResponse {
    documentNumber: string;
    importedItems: number;
    stockedItems: number;
    updatedPassports: number;
}

export const importWarehousePdfDocument = (payload: unknown): Promise<WarehousePdfImportResponse> => {
    return interceptor.post('/warehouse-service/api/resources/importPdfDocument', payload, {
        skipErrorInterceptor: true,
    });
};

export const updatePassportDescriptionMapping = (payload: {
    correctPassportId: number;
    description: string;
    wrongPassportId: number;
}): Promise<void> => {
    return interceptor.post('/warehouse-service/api/resources/passport-description-mapping', payload);
};

export const useGetAllBatches = (props: TableRequestParams, aggregateBatches?: boolean): Promise<TableData<Batch[]>> => {
    let searchBy = props.filter?.search ? props.filter.search : null;
    return interceptor.get('/warehouse-service/api/batch/getAllBatchesForWarehouse', {
        params: {
            warehouseId: props.filter?.id,
            page: props['pager.page'],
            itemsPerPage: props['pager.itemsPerPage'],
            searchBy,
        },
        transformResponse: formatCostField,
    });
};

export const useGetAllCompletedHandouts = (props: TableRequestParams): Promise<TableData<CompletedHandoutGroup[]>> => {
    let searchBy = props.filter?.search ? props.filter.search : null;
    return interceptor.get('/warehouse-service/api/inventory/getItemReceipts', {
        params: {
            warehouseId: props.filter?.id,
            page: props['pager.page'],
            itemsPerPage: props['pager.itemsPerPage'],
            searchBy,
        },
        transformResponse: formatPriceField,
    });
};

export const useGetHandoutRequests = (props: TableRequestParams): Promise<TableData<HandoutRequest[]>> => {
    return interceptor.get('/warehouse-service/api/inventory/handoutRequests', {
        params: {
            warehouseId: props.filter?.id,
            userId: props.filter?.assignedDistributor,
            status: props.filter?.state,
            page: props['pager.page'],
            itemsPerPage: props['pager.itemsPerPage'],
        },
        transformResponse: transformTableResponse,
    });
};

export const getHandoutRequestsRevision = (props: TableRequestParams): Promise<HandoutRequestsRevision> => {
    return interceptor.get<HandoutRequestsRevision, HandoutRequestsRevision>(
        '/warehouse-service/api/inventory/handoutRequests/revision',
        {
            params: {
                warehouseId: props.filter?.id,
                userId: props.filter?.assignedDistributor,
                status: props.filter?.state,
            },
        }
    );
};

export const getHandoutRequest = (requestId: number): Promise<HandoutRequest> =>
    interceptor.get(`/warehouse-service/api/inventory/handoutRequests/${requestId}`, {
        skipErrorInterceptor: true,
    });

export const prepareHandoutRequest = (
    requestId: number,
    expectedVersion: number | null,
    warehouseId: number,
    allocations: { requestItemId: number; reservationId: number }[],
): Promise<HandoutPreparation> => interceptor.post(
    `/warehouse-service/api/inventory/handoutRequests/${requestId}/prepare`,
    { expectedVersion, warehouseId, allocations },
    { skipErrorInterceptor: true },
);

export const getHandoutPreparation = (requestId: number): Promise<HandoutPreparation> =>
    interceptor.get(`/warehouse-service/api/inventory/handoutRequests/${requestId}/preparation`, {
        skipErrorInterceptor: true,
    });

export const releaseHandoutPreparation = (requestId: number): Promise<void> =>
    interceptor.post(`/warehouse-service/api/inventory/handoutRequests/${requestId}/release`, {}, {
        skipErrorInterceptor: true,
    });

export const issuePreparedHandoutRequest = (requestId: number, preparationToken: string): Promise<void> =>
    interceptor.post(`/warehouse-service/api/inventory/handoutRequests/${requestId}/issue`, {
        preparationToken,
    }, { skipErrorInterceptor: true });

export const deleteHandoutRequest = (requestId: number): Promise<void> => {
    return interceptor.delete('/warehouse-service/api/inventory/handoutRequests', {
        params: {
            requestId,
        },
    });
};

export const deleteHandoutRequestItem = (requestItemId: number): Promise<void> => {
    return interceptor.delete('/warehouse-service/api/inventory/handoutRequests/items', {
        params: {
            requestItemId,
        },
    });
};

export const useGetAllScraps = (props: TableRequestParams): Promise<TableData<Scrap[]>> => {
    let searchBy = props.filter?.search ? props.filter.search : null;
    return interceptor.get('/warehouse-service/api/warehouse/getScrapsForWarehouse', {
        params: {
            warehouseId: props.filter?.id,
            page: props['pager.page'],
            itemsPerPage: props['pager.itemsPerPage'],
            searchBy,
        },
        transformResponse: transformTableResponse,
    });
};

export const useGetUserInventory = (
    paginationOrProps: Pagination | TableRequestParams,
    filter?: InventoryFilter
): Promise<TableData<InventoryContent[]>> => {
    const isPagination = 'currentPage' in paginationOrProps;
    const requestFilter = (isPagination ? filter : paginationOrProps.filter) as InventoryFilter | undefined;
    const page = isPagination ? paginationOrProps.currentPage : paginationOrProps['pager.page'] ?? paginationOrProps.pagination?.currentPage;
    const itemsPerPage = isPagination ? paginationOrProps.pageSize : paginationOrProps['pager.itemsPerPage'] ?? paginationOrProps.pagination?.pageSize;
    let searchBy = requestFilter?.search ? requestFilter.search : null;

    return interceptor.get('/warehouse-service/api/inventory/getUserInventory', {
        params: {
            page,
            itemsPerPage,
            searchBy,
            userId: requestFilter?.id,
            forSale: requestFilter?.forSale ? true : undefined
        },
        //transformResponse: formatInventoryContentByPassport,
    });
};

export const getAllInventories = (props: TableRequestParams): Promise<TableData<InventoryContent[]>> => {
    const requestFilter = props.filter as InventoryFilter | undefined;
    const searchBy = requestFilter?.search ? requestFilter.search : null;

    return interceptor.get('/warehouse-service/api/inventory/allInventories', {
        params: {
            page: props['pager.page'] ?? props.pagination?.currentPage,
            itemsPerPage: props['pager.itemsPerPage'] ?? props.pagination?.pageSize,
            searchBy,
            forSale: requestFilter?.forSale ? true : undefined,
        },
    });
};

export const useGetUserAvailableItemsIds = (id?: number): Promise<number[]> => {
    return interceptor.get('/warehouse-service/api/inventory/getUserAvailableItemsIds/' + id);
};

export const manualHandout = (fields: ManualHandoutRequest): Promise<void> => {
    return interceptor.post('/warehouse-service/api/inventory/manualGiveHandoutToUser', {
        ...fields,
    });
};

export const reserveHandoutQuantity = (
    fields: HandoutReservationRequest
): Promise<HandoutReservationResponse> => {
    return interceptor.post('/warehouse-service/api/inventory/reservations', fields, {
        skipErrorInterceptor: true,
    });
};

export const getHandoutReservations = (warehouseId: number): Promise<HandoutReservationResponse[]> => {
    return interceptor.get('/warehouse-service/api/inventory/reservations', {
        params: {
            warehouseId,
        },
        skipErrorInterceptor: true,
    });
};

export const releaseHandoutReservation = (reservationId: number): Promise<void> => {
    return interceptor.delete(`/warehouse-service/api/inventory/reservations/${reservationId}`, {
        skipErrorInterceptor: true,
    });
};

export const scrapItem = (fields: InventoryContentForTask) => {
    return interceptor.put('/warehouse-service/api/inventory/scrapItem', {
        ...fields,
    });
};

export const getQuantityCount = (
    passport: string | undefined,
    warehouseId: string | undefined,
): Promise<QuantityCountList> => {
    return interceptor.get('/warehouse-service/api/inventory/getQuantityCount', {
        params: {
            passport,
            warehouseId,
        },
    });
};

export const useAddBatch = (fields: Batch, warehouseId: string | undefined) => {
    fields.cost *= 100;
    return interceptor.post('/warehouse-service/api/batch/addBatchWithDevices', {
        batchCode: fields.batchCode,
        devices: fields.deviceList,
        warehouseId: warehouseId,
    });
};

export const useDeleteBatch = (fields: Batch) => {
    return interceptor.delete('/warehouse-service/api/batch/deleteBatch', {
        params: {
            id: fields.id,
        },
    });
};

export const useUpdateBatch = (fields: Batch, id: number | undefined) => {
    fields.cost *= 100;
    return interceptor.put(`/warehouse-service/api/batch/updateBatch`, {
        ...fields,
        id: id,
    });
};

export const formatCostBatchField = (data: string) => {
    const r = JSON.parse(data);
    r.map((item: ItemQuantity) => (item.cost /= 100));
    return r;
};

export const useGetPassportsForBatchCode = (batchCode?: string): Promise<ItemQuantity[]> => {
    return interceptor.get('/warehouse-service/api/resources/getQuantitiesByBatchByCode/', {
        params: {
            batchCode,
        },
        transformResponse: formatCostBatchField,
    });
};

export const useGetPassportImageUrls = (passportId: number): Promise<string[]> => {
    return interceptor.get('/warehouse-service/api/images/getImagesForPassport', {
        params: {
            passportId,
        },
    });
};
export const useDeletePassportImages = (passportId?: number) => {
    if (passportId)
        return interceptor.put(
            '/warehouse-service/api/images/removeAllImagesFromPassport',
            {},
            {
                params: {
                    passportId,
                },
            },
        );
};

export const useGetPassportImages = (url: string[]): Promise<File[]> => {
    const promiseArray: Promise<File>[] = [];
    url?.map((value) =>
        promiseArray.push(
            interceptor.get(value, {
                responseType: 'blob',
            }),
        ),
    );
    return Promise.all(promiseArray);
};

export const addImages = (passportId: number, img?: File[]) => {
    const formData = new FormData();
    if (img) img.forEach((i) => formData.append('img', i));
    return interceptor.put('/warehouse-service/api/images/addImgToPassport', formData, {
        params: {
            passportId,
        },
        headers: { 'Content-Type': 'multipart/form-data' },
    });
};
export const getHandoversSummary = (
    props: TableRequestParams,
    branchId?: number
): Promise<HandoversSummary> => {
    let searchBy = props.filter?.search ? props.filter.search : null;
    const taskCategory = props.filter?.type ? props.filter.type : null;

    return interceptor.get(
        '/warehouse-service/docs/handovers/summary',
        {
            params: {
                from: props.filter?.after,
                to: props.filter?.before,
                branchId,
                searchBy,
                distributorId: props.filter?.id,
                taskCategory,
            }
        }
    ) as unknown as Promise<HandoversSummary>;
};

export const getHandover = (handover: HandoverProps): Promise<Blob> => {
    if (!handover.save) handover.save = false;
    return interceptor.post(
        '/warehouse-service/docs/handover',
        { ...handover },
        {
            responseType: 'blob',
            transformResponse: (data) => {
                return new Blob([data], { type: 'application/pdf' });
            },
        },
    );
};
export const getHandoverByTaskId = (taskId: number): Promise<HandoverProtocol> => {
    return interceptor.get(
        '/warehouse-service/docs/handoverByTaskId',
        {
            params: { taskId },
            skipErrorInterceptor: true,
        },
    );
};

export const getRentalAgreement = (rentalAgreementProps: RentalAgreementProps): Promise<Blob> => {
    return interceptor.post(
        '/warehouse-service/docs/rentalAgreement',
        { ...rentalAgreementProps },
        {
            responseType: 'blob',
            transformResponse: (data) => {
                return new Blob([data], { type: 'application/pdf' });
            },
        },
    );
};
export const getWarehouseReceipt = (warehouseReceiptProps: WarehouseReceiptProps): Promise<Blob> => {
    return interceptor.post(
        '/warehouse-service/docs/receipt',
        { ...warehouseReceiptProps, date: dateFormat(new Date(warehouseReceiptProps.date), date) },
        {
            responseType: 'blob',
            transformResponse: (data) => {
                return new Blob([data], { type: 'application/pdf' });
            },
        },
    );
};

export const getPricesOfPassports = async (payload: PassportSumPriceRequestDTO[]): Promise<number> => {
    return await interceptor.post('/warehouse-service/api/resources/passportsPriceSum', payload);
};

export const getDevicesModel = (): Promise<AnyPassport[]> => {
    return interceptor.get(`/warehouse-service/api/resources/getDevicesModel`);
};

export const getMissingItemsForTasks = (userId: number, taskResources: ItemRequest[]): Promise<ItemRequest[]> => {
    if (taskResources.length === 0) return Promise.resolve<ItemRequest[]>([]);
    return interceptor.post(
        `/warehouse-service/api/inventory/getListOfMissingItems`,
        {
            neededItems: taskResources,
        },
        {
            params: {
                userId,
            },
            transformResponse: transformNoPagerResponse,
        },
    );
};
export const checkForItemsInInventory = (userId: number, taskResources: ItemRequest[]): Promise<InventoryCheckup> => {
    return interceptor.post(
        `/warehouse-service/api/inventory/checkInventoryForItems`,
        {
            neededItems: taskResources,
        },
        {
            params: {
                userId,
            },
        },
    );
};

export const manualHandoutBatch = (request: import('./manual-handout-operation').ManualOperation['request']): Promise<{operationId: string; receiptIds: number[]}> =>
    interceptor.post('/warehouse-service/api/inventory/manualHandouts/batch', request);

export const reserveManualHandout = (request: import('./manual-handout-operation').ManualReservationRequest):
    Promise<import('./manual-handout-operation').ManualReservationSnapshot> =>
    interceptor.post('/warehouse-service/api/inventory/manualHandouts/reservations', request, {skipErrorInterceptor: true});

export const cancelManualHandout = (request: import('./manual-handout-operation').ManualReservationRequest): Promise<void> =>
    interceptor.post('/warehouse-service/api/inventory/manualHandouts/reservations/cancel', request, {skipErrorInterceptor: true});
